import { FC, useMemo } from 'react';
import { useTheme } from '../../../context/ThemeContext';

interface RadarChartProps {
  /** Vintage score (0-100) */
  vintage: number;
  /** Critic score (0-100) */
  critic: number;
  /** Fair Value score (0-100) */
  fairValue: number;
  /** Liquidity score (0-100) */
  liquidity: number;
  /** Performance score (0-100) */
  performance: number;
  /** Size of the chart */
  size?: number;
  /** Callback when hovering a metric */
  onHover?: (label: string | null, value: number | null, description: string | null) => void;
}

const metricDescriptions: Record<string, string> = {
  Vintage: 'Quality rating based on the vintage year conditions',
  'Norm. Critic': 'Normalised score from professional wine critics',
  'Fair Value': 'How fairly priced the wine is relative to market',
  Liquidity: 'Ease of buying or selling this wine',
  'Perf.': 'Historical price performance score',
};

const QualityRadarChart: FC<RadarChartProps> = ({
  vintage,
  critic,
  fairValue,
  liquidity,
  performance,
  size = 160,
  onHover,
}) => {
  const { isDark } = useTheme();
  const center = size / 2;
  const maxRadius = (size / 2) * 0.48; // Smaller chart to leave more room for labels

  // Pentagon angles (starting from top, going clockwise)
  const angles = [
    -90, // Vintage (top)
    -90 + 72, // Critic (top-right)
    -90 + 144, // Fair Value (bottom-right)
    -90 + 216, // Liquidity (bottom-left)
    -90 + 288, // Perf (top-left)
  ];

  const toRadians = (deg: number) => (deg * Math.PI) / 180;

  const getPoint = (angle: number, value: number) => {
    const radius = (value / 100) * maxRadius;
    const rad = toRadians(angle);
    return {
      x: center + radius * Math.cos(rad),
      y: center + radius * Math.sin(rad),
    };
  };

  const getLabelPoint = (angle: number) => {
    // Adjust radius based on position - keep labels within bounds
    const baseRadius = maxRadius + 22;
    const rad = toRadians(angle);
    const x = center + baseRadius * Math.cos(rad);
    const y = center + baseRadius * Math.sin(rad);

    return { x, y };
  };

  const values = [vintage, critic, fairValue, liquidity, performance];
  const labels = ['Vintage', 'Norm. Critic', 'Fair Value', 'Liquidity', 'Perf.'];

  const gridLevels = [25, 50, 75, 100];

  // Theme-aware colors
  const colors = useMemo(
    () => ({
      grid: isDark ? '#475569' : '#e5e7eb',
      axis: isDark ? '#64748b' : '#d1d5db',
      polygon: isDark ? 'rgba(148, 163, 184, 0.3)' : 'rgba(107, 114, 128, 0.3)',
      polygonStroke: isDark ? '#94a3b8' : '#4b5563',
      point: isDark ? '#e2e8f0' : '#374151',
      label: isDark ? '#94a3b8' : '#6b7280',
      value: isDark ? '#e2e8f0' : '#374151',
    }),
    [isDark],
  );

  const gridPaths = useMemo(() => {
    return gridLevels.map((level) => {
      const points = angles.map((angle) => getPoint(angle, level));
      return points.map((p) => `${p.x},${p.y}`).join(' ');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  const axisLines = useMemo(() => {
    return angles.map((angle) => {
      const end = getPoint(angle, 100);
      return { x1: center, y1: center, x2: end.x, y2: end.y };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  const dataPath = useMemo(() => {
    const points = values.map((value, i) => getPoint(angles[i], value));
    return points.map((p) => `${p.x},${p.y}`).join(' ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, size]);

  const labelPositions = useMemo(() => {
    return angles.map((angle, i) => {
      const pos = getLabelPoint(angle);
      // Adjust text anchor based on position for proper alignment
      let textAnchor: 'start' | 'middle' | 'end' = 'middle';
      // Top (Vintage) - center aligned
      if (Math.abs(angle + 90) < 15) {
        textAnchor = 'middle';
      }
      // Right side (Critic, Fair Value) - left aligned from point
      else if (angle > -75 && angle < 90) {
        textAnchor = 'start';
      }
      // Left side (Liquidity, Perf) - right aligned from point
      else {
        textAnchor = 'end';
      }

      return {
        ...pos,
        angle,
        label: labels[i],
        value: values[i],
        textAnchor,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, size]);

  const handleMouseEnter = (label: string, value: number) => {
    if (onHover) {
      onHover(label, value, metricDescriptions[label] || '');
    }
  };

  const handleMouseLeave = () => {
    if (onHover) {
      onHover(null, null, null);
    }
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
      {/* Grid lines */}
      {gridPaths.map((path, i) => (
        <polygon key={`grid-${i}`} points={path} fill="none" stroke={colors.grid} strokeWidth="1" />
      ))}

      {/* Axis lines */}
      {axisLines.map((line, i) => (
        <line
          key={`axis-${i}`}
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke={colors.axis}
          strokeWidth="1"
        />
      ))}

      {/* Data polygon */}
      <polygon points={dataPath} fill={colors.polygon} stroke={colors.polygonStroke} strokeWidth="1.5" />

      {/* Data points with hover areas */}
      {values.map((value, i) => {
        const point = getPoint(angles[i], value);
        return (
          <g key={`point-${i}`}>
            <circle
              cx={point.x}
              cy={point.y}
              r="10"
              fill="transparent"
              className="cursor-pointer"
              onMouseEnter={() => handleMouseEnter(labels[i], value)}
              onMouseLeave={handleMouseLeave}
            />
            <circle cx={point.x} cy={point.y} r="3" fill={colors.point} />
          </g>
        );
      })}

      {/* Labels with values */}
      {labelPositions.map((pos, i) => (
        <g
          key={`label-${i}`}
          className="cursor-pointer"
          onMouseEnter={() => handleMouseEnter(pos.label, pos.value)}
          onMouseLeave={handleMouseLeave}
        >
          <text
            x={pos.x}
            y={pos.y - 5}
            textAnchor={pos.textAnchor}
            dominantBaseline="middle"
            style={{ fontSize: '9px', fill: colors.label }}
          >
            {pos.label}
          </text>
          <text
            x={pos.x}
            y={pos.y + 7}
            textAnchor={pos.textAnchor}
            dominantBaseline="middle"
            style={{ fontSize: '10px', fontWeight: 600, fill: colors.value }}
          >
            {pos.value ? Math.round(pos.value) : '-'}
          </text>
        </g>
      ))}
    </svg>
  );
};

export default QualityRadarChart;
